import { useCallback, useEffect, useRef } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'

// 공용 roving keyboard 훅 — Dropdown/Menu/Tree/Tabs 가 공유하는 화살표·Home/End·
// Enter/Space·type-ahead 네비게이션 로직. 각 소비 컴포넌트는 자신의 평탄화된 항목
// 리스트(개수 + 활성 인덱스)를 넘겨 재사용한다.
//
// - orientation: 'vertical' = Up/Down 이동, 'horizontal' = Left/Right 이동.
// - onArrowLeft/onArrowRight: 트리 등에서 좌/우 키를 확장·축소로 전용할 때 사용.
//   true 를 반환하면 기본(수평 이동) 처리를 건너뛴다.
// - getLabel: type-ahead 문자 매칭용 라벨(소문자 비교). 없으면 type-ahead 비활성.

export interface RovingKeydownOptions {
  itemCount: number
  activeIndex: number
  setActiveIndex: (index: number) => void
  onActivate?: (index: number) => void
  isDisabled?: (index: number) => boolean
  getLabel?: (index: number) => string
  orientation?: 'vertical' | 'horizontal'
  /** 끝에서 반대편으로 순환 이동(기본 true). */
  loop?: boolean
  /** 좌측 키 전용 처리(트리 축소/부모 이동). true 반환 시 기본 이동 억제. */
  onArrowLeft?: (index: number) => boolean | void
  /** 우측 키 전용 처리(트리 확장/자식 이동). true 반환 시 기본 이동 억제. */
  onArrowRight?: (index: number) => boolean | void
}

const TYPEAHEAD_RESET_MS = 500

export function useRovingKeydown(options: RovingKeydownOptions) {
  const {
    itemCount,
    activeIndex,
    setActiveIndex,
    onActivate,
    isDisabled,
    getLabel,
    orientation = 'vertical',
    loop = true,
    onArrowLeft,
    onArrowRight,
  } = options

  const typeaheadBuffer = useRef('')
  const typeaheadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTypeahead = useCallback(() => {
    if (typeaheadTimer.current != null) {
      clearTimeout(typeaheadTimer.current)
      typeaheadTimer.current = null
    }
  }, [])

  // 언마운트 시 type-ahead 버퍼 타이머 정리(CLAUDE.md: cleanup 없는 setTimeout 금지)
  useEffect(() => clearTypeahead, [clearTypeahead])

  const disabledAt = useCallback((i: number) => isDisabled?.(i) ?? false, [isDisabled])

  // dir 방향의 첫 활성 인덱스(Home=1, End=-1, 초기 진입). 없으면 -1.
  const firstEnabled = useCallback(
    (dir: 1 | -1): number => {
      const start = dir === 1 ? 0 : itemCount - 1
      for (let i = 0; i < itemCount; i++) {
        const idx = dir === 1 ? start + i : start - i
        if (!disabledAt(idx)) return idx
      }
      return -1
    },
    [itemCount, disabledAt],
  )

  // from 에서 dir 방향으로 disabled 를 건너뛴 첫 인덱스. 없으면 from 유지.
  const nextEnabled = useCallback(
    (from: number, dir: 1 | -1): number => {
      if (itemCount === 0) return -1
      let i = from
      for (let step = 0; step < itemCount; step++) {
        let candidate = i + dir
        if (candidate < 0 || candidate >= itemCount) {
          if (!loop) return from < 0 ? firstEnabled(dir) : from
          candidate = (candidate + itemCount) % itemCount
        }
        i = candidate
        if (!disabledAt(i)) return i
      }
      return from
    },
    [itemCount, loop, disabledAt, firstEnabled],
  )

  const move = useCallback(
    (dir: 1 | -1) => {
      const target = activeIndex < 0 ? firstEnabled(dir) : nextEnabled(activeIndex, dir)
      if (target >= 0) setActiveIndex(target)
    },
    [activeIndex, firstEnabled, nextEnabled, setActiveIndex],
  )

  const runTypeahead = useCallback(
    (char: string) => {
      if (!getLabel || itemCount === 0) return
      clearTypeahead()
      typeaheadBuffer.current += char.toLowerCase()
      const buffer = typeaheadBuffer.current
      // 활성 항목 다음부터 순환 검색(단일 문자 반복 입력 시 다음 매칭으로 이동)
      const startFrom = buffer.length === 1 ? activeIndex + 1 : activeIndex
      for (let step = 0; step < itemCount; step++) {
        const idx = (((startFrom + step) % itemCount) + itemCount) % itemCount
        if (disabledAt(idx)) continue
        if (getLabel(idx).toLowerCase().startsWith(buffer)) {
          setActiveIndex(idx)
          break
        }
      }
      typeaheadTimer.current = setTimeout(() => {
        typeaheadBuffer.current = ''
        typeaheadTimer.current = null
      }, TYPEAHEAD_RESET_MS)
    },
    [getLabel, itemCount, activeIndex, clearTypeahead, disabledAt, setActiveIndex],
  )

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      // 편집 가능한 자손(검색 input·트리 rename 등)에 포커스가 있으면 로빙을 건드리지 않는다.
      // (컨테이너 onKeyDown 으로 버블된 타이핑/화살표를 preventDefault 로 가로채면 입력이 깨진다.)
      const target = e.target as HTMLElement | null
      if (target != null && target !== e.currentTarget) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return
        }
      }

      const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight'
      const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft'

      switch (e.key) {
        case nextKey:
          e.preventDefault()
          move(1)
          return
        case prevKey:
          e.preventDefault()
          move(-1)
          return
        case 'Home':
          e.preventDefault()
          setActiveIndex(firstEnabled(1))
          return
        case 'End':
          e.preventDefault()
          setActiveIndex(firstEnabled(-1))
          return
        case 'Enter':
        case ' ':
          if (activeIndex >= 0 && !disabledAt(activeIndex)) {
            e.preventDefault()
            onActivate?.(activeIndex)
          }
          return
        default:
          break
      }

      // 트리 등: 주 축과 직교하는 좌/우 키 전용 처리
      if (e.key === 'ArrowLeft' && onArrowLeft) {
        if (onArrowLeft(activeIndex) !== false) e.preventDefault()
        return
      }
      if (e.key === 'ArrowRight' && onArrowRight) {
        if (onArrowRight(activeIndex) !== false) e.preventDefault()
        return
      }

      // type-ahead: 수식 없는 단일 인쇄 가능 문자
      if (
        getLabel &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        e.key !== ' '
      ) {
        e.preventDefault()
        runTypeahead(e.key)
      }
    },
    [
      orientation,
      move,
      setActiveIndex,
      firstEnabled,
      activeIndex,
      disabledAt,
      onActivate,
      onArrowLeft,
      onArrowRight,
      getLabel,
      runTypeahead,
    ],
  )

  return { onKeyDown }
}
